import React from 'react';

type Options<T> = {
  parse?: (raw: string) => T;
  serialize?: (value: T) => string;
};

export function useLocalStorageState<T>(
  key: string,
  initialValue: T,
  options: Options<T> = {}
) {
  const parse = React.useCallback(
    (raw: string) => (options.parse ? options.parse(raw) : (JSON.parse(raw) as T)),
    [options.parse]
  );
  const serialize = React.useCallback(
    (value: T) => (options.serialize ? options.serialize(value) : JSON.stringify(value)),
    [options.serialize]
  );

  const [value, setValue] = React.useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw == null) return initialValue;
      return parse(raw);
    } catch {
      return initialValue;
    }
  });

  React.useEffect(() => {
    try {
      window.localStorage.setItem(key, serialize(value));
    } catch {
      // ignore
    }
  }, [key, serialize, value]);

  return [value, setValue] as const;
}


